import clsx from "clsx";
import React from "react";
import {
  Select,
  MenuItem,
  ListItemText,
  SelectProps,
  useMediaQuery,
  useTheme,
  Typography,
  IconButton,
  Box,
  SelectChangeEvent,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDownUp } from "lucide-react";

import { ActivitySort, activitySortOptions } from "../../constants";
import { filterInputHeightProps } from "../../components/coach-programs/CoachProgramsFilter";

const useStyles = makeStyles((theme) => ({
  select: {
    backgroundColor: theme.palette.background.paper,
    ...filterInputHeightProps,
  },
  hiddenSelect: {
    position: "absolute",
    top: 0,
    left: 0,
    opacity: 0,
    width: "100%",
    height: "100%",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& fieldset": {
      border: "none",
    },
  },
  iconButton: {
    width: 40,
    height: 40,
    backgroundColor: theme.palette.background.paper,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  inputActive: {
    "& .MuiOutlinedInput-root fieldset": {
      borderColor: theme.palette.divider,
    },
    borderColor: theme.palette.text.disabled,
  },
  primaryText: {
    fontWeight: 500,
    fontSize: 15,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap",
  },
}));

export interface ActivitySortFilterProps
  extends Omit<SelectProps<ActivitySort>, "onChange" | "variant"> {
  sortedBy: ActivitySort;
  active?: boolean;
  onChange: (value: ActivitySort) => void;
}

export function ActivitySortFilter(props: ActivitySortFilterProps) {
  const { className, sortedBy, active, onChange, ...other } = props;
  const s = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [open, setOpen] = React.useState(false);

  const handleSelectChange = (event: SelectChangeEvent<ActivitySort>) => {
    onChange(event.target.value as ActivitySort);
  };

  const menuItems = Object.entries(activitySortOptions).map(([key, label]) => (
    <MenuItem key={key} value={key}>
      <ListItemText classes={{ primary: s.primaryText }} primary={label} />
    </MenuItem>
  ));

  if (isMobile) {
    return (
      <Box position="relative">
        <IconButton
          className={clsx(s.iconButton, active && s.inputActive, className)}
          onClick={() => setOpen(true)}
        >
          <ArrowDownUp
            size={24}
            color={
              active ? theme.palette.text.primary : theme.palette.text.secondary
            }
          />
        </IconButton>

        <Select<ActivitySort>
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          value={sortedBy}
          onChange={handleSelectChange}
          variant="outlined"
          className={s.hiddenSelect}
          {...(other as any)}
        >
          {menuItems}
        </Select>
      </Box>
    );
  }

  return (
    <Select<ActivitySort>
      className={clsx(s.select, active && s.inputActive, className)}
      value={sortedBy}
      onChange={handleSelectChange}
      variant="outlined"
      renderValue={() => (
        <Typography className={s.primaryText}>
          Sort by: {activitySortOptions[sortedBy]}
        </Typography>
      )}
      {...(other as any)}
    >
      {menuItems}
    </Select>
  );
}
